/**
 * LeaguePlayersPage — /league/:leagueId/players
 * ─────────────────────────────────────────────────────────────────
 * Phase 3. Season-scoped player management surface with three tabs:
 *
 *   1. Pool       — every player registered for the season; CSV
 *                   import, manual add, edit, withdraw.
 *   2. By team    — the rosters laid out per team; click-to-
 *                   allocate-or-transfer; click-to-unassign.
 *   3. Transfers  — append-only audit log of player movements.
 *
 * The page is season-scoped because the player pool itself is
 * per-season (resolved decision #8). A season picker at the top
 * lets you switch.
 *
 * Permissions (mirror backend):
 *   • Viewing any tab:     league_member
 *   • Pool add/edit/CSV:   manage_players
 *   • Allocate/transfer:   manage_players
 *
 * Page intentionally avoids drag-and-drop in this initial version
 * — clicks/menus keep the interaction model simple and mobile-
 * friendly. A future enhancement could layer DnD on top of the
 * same click-target allocation API.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  Users, UserPlus, Upload, Calendar, ArrowRight, X, Save, Trash2,
  Edit2, RefreshCw, Search, Hash, Mail,
  CheckCircle2, AlertTriangle, Plus, FileText, ArrowLeftRight,
  ChevronDown,
  // Phase H (league gap-closure) — discipline tab.
  Gavel,
} from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import LeagueSubNav from './LeagueSubNav.jsx';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';

export default function LeaguePlayersPage() {
  const { leagueId } = useParams();
  const { isManager, can, loading: roleLoading } = useLeagueMembership(leagueId);
  const canManage = isManager || can('manage_players');

  const [league, setLeague]   = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonIdState] = useState('');
  // 2026-09-28 — ?season=<id> deep link; the picker keeps it in sync.
  const [searchParams, setSearchParams] = useSearchParams();
  const setSeasonId = (id) => {
    setSeasonIdState(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set('season', id); else next.delete('season');
    setSearchParams(next, { replace: true });
  };
  const [tab, setTab] = useState('pool');  // 'pool' | 'byteam' | 'transfers'
  const [loading, setLoading] = useState(true);

  const defaultSeasonId = useMemo(() => {
    if (seasons.length === 0) return '';
    const wanted = searchParams.get('season');
    if (wanted && seasons.some(s => s.id === wanted)) return wanted;
    const active = seasons.find(s => s.status === 'active');
    return active?.id || seasons[0].id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasons]);

  async function loadShell() {
    if (!leagueId) return;
    try {
      const [leagueRes, seasonsRes] = await Promise.all([
        api.get(`/leagues/${leagueId}`),
        api.get(`/leagues/${leagueId}/seasons`),
      ]);
      setLeague(leagueRes?.data?.league || null);
      setSeasons(seasonsRes?.data?.seasons || []);
    } catch (err) {
      if (err?.response?.status === 403) toast.error('You are not a member of this league');
      else if (err?.response?.status !== 401) toast.error('Could not load league');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadShell(); /* eslint-disable-next-line */ }, [leagueId]);

  useEffect(() => {
    if (!seasonId && defaultSeasonId) setSeasonIdState(defaultSeasonId);
  }, [defaultSeasonId, seasonId]);

  if (loading || roleLoading) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 1080 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>
      </div>
    );
  }
  if (!league) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 1080 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>
          League not found or no access.
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" style={{ maxWidth: 1080 }}>
      <LeagueSubNav league={league} />

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 className="section-title text-xl">Players</h2>
          <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
            Manage the player pool for a season, allocate players to teams, and track transfers.
          </p>
        </div>
      </div>

      {seasons.length === 0 ? (
        <EmptyNoSeasonsCard leagueId={leagueId} />
      ) : (
        <>
          {/* Season picker */}
          <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Calendar size={16} color="var(--ice)" />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Season:</span>
            <select value={seasonId}
              onChange={(e) => setSeasonId(e.target.value)}
              style={{ ...inputStyle, width: 'auto', minWidth: 240, flex: '1 1 280px' }}>
              {seasons.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.status})
                </option>
              ))}
            </select>
          </div>

          {/* Tabs */}
          <div style={{
            display: 'flex', gap: 4, padding: 4,
            background: 'var(--surface-2)', borderRadius: 12,
            border: '1px solid var(--border)', marginBottom: 16, flexWrap: 'wrap',
          }}>
            <TabButton active={tab === 'pool'}      onClick={() => setTab('pool')}      icon={Users}>Pool</TabButton>
            <TabButton active={tab === 'byteam'}    onClick={() => setTab('byteam')}    icon={ArrowLeftRight}>By team</TabButton>
            <TabButton active={tab === 'transfers'} onClick={() => setTab('transfers')} icon={FileText}>Transfers</TabButton>
            {/* Phase H — discipline/suspension tracking. */}
            <TabButton active={tab === 'discipline'} onClick={() => setTab('discipline')} icon={Gavel}>Discipline</TabButton>
          </div>

          {seasonId && tab === 'pool'      && <PoolTab      leagueId={leagueId} seasonId={seasonId} canManage={canManage} />}
          {seasonId && tab === 'byteam'    && <ByTeamTab    leagueId={leagueId} seasonId={seasonId} canManage={canManage} />}
          {seasonId && tab === 'transfers' && <TransfersTab leagueId={leagueId} seasonId={seasonId} />}
          {tab === 'discipline' && <DisciplineTab leagueId={leagueId} seasonId={seasonId} canManage={canManage} />}
        </>
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '8px 14px', borderRadius: 8,
      fontSize: 13, fontWeight: 600,
      color: active ? 'var(--text)' : 'var(--text-2)',
      background: active ? 'var(--surface)' : 'transparent',
      border: 'none',
      boxShadow: active ? 'var(--shadow-sm)' : 'none',
      cursor: 'pointer',
      transition: 'all 0.15s',
    }}>
      <Icon size={14} />
      {children}
    </button>
  );
}

function EmptyNoSeasonsCard({ leagueId }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 36 }}>
      <Calendar size={36} color="var(--text-2)" style={{ margin: '0 auto 14px', opacity: 0.5 }} />
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>No seasons yet</div>
      <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 18 }}>
        Players belong to a season. Create a season first, then come back here.
      </div>
      <Link to={`/league/${leagueId}/seasons`} className="btn-ice"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Plus size={14} />
        Go to Seasons
        <ArrowRight size={12} />
      </Link>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// POOL TAB
// ═══════════════════════════════════════════════════════════════

function PoolTab({ leagueId, seasonId, canManage }) {
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');  // all|available|assigned|free_agent|withdrawn
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd]       = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const url = statusFilter === 'all'
        ? `/leagues/${leagueId}/seasons/${seasonId}/players/pool`
        : `/leagues/${leagueId}/seasons/${seasonId}/players/pool?status=${statusFilter}`;
      const { data } = await api.get(url);
      setPool(data?.pool || []);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error('Could not load pool');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [seasonId, statusFilter]);

  // Client-side search across decrypted display names + player_uids.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(p =>
      (p.display_name || '').toLowerCase().includes(q) ||
      (p.player_uid   || '').toLowerCase().includes(q)
    );
  }, [pool, search]);

  // Counts per status for the filter pills.
  const counts = useMemo(() => ({
    all:        pool.length,
    available:  pool.filter(p => p.status === 'available').length,
    assigned:   pool.filter(p => p.status === 'assigned').length,
    free_agent: pool.filter(p => p.status === 'free_agent').length,
    withdrawn:  pool.filter(p => p.status === 'withdrawn').length,
  }), [pool]);

  return (
    <>
      {/* Actions row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', position: 'relative' }}>
          <Search size={14} color="var(--text-2)" style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
          }} />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or PLR-…" style={{ ...inputStyle, paddingLeft: 34 }} />
        </div>
        {canManage && (
          <>
            <button type="button" className="btn-ghost"
              onClick={() => setShowImport(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Upload size={13} />
              Import CSV
            </button>
            <button type="button" className="btn-ice"
              onClick={() => setShowAdd(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <UserPlus size={13} />
              Add player
            </button>
          </>
        )}
      </div>

      {/* Status filter pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { v: 'all',        l: 'All' },
          { v: 'available',  l: 'Available' },
          { v: 'assigned',   l: 'Assigned' },
          { v: 'free_agent', l: 'Free agents' },
          { v: 'withdrawn',  l: 'Withdrawn' },
        ].map(s => (
          <FilterPill key={s.v}
            active={statusFilter === s.v}
            onClick={() => setStatusFilter(s.v)}
            count={counts[s.v]}>
            {s.l}
          </FilterPill>
        ))}
      </div>

      {loading ? (
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <PoolEmpty canManage={canManage} hasFilter={statusFilter !== 'all' || search.trim().length > 0}
          onAdd={() => setShowAdd(true)} onImport={() => setShowImport(true)} />
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {filtered.map((p, i) => (
            <PoolRow key={p.id} entry={p} isLast={i === filtered.length - 1}
              canManage={canManage} onEdit={() => setEditing(p)} onChanged={load} />
          ))}
        </div>
      )}

      {showAdd && (
        <AddPlayerModal leagueId={leagueId} seasonId={seasonId}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); load(); }} />
      )}
      {showImport && (
        <ImportCsvModal leagueId={leagueId} seasonId={seasonId}
          onClose={() => setShowImport(false)}
          onCompleted={() => { setShowImport(false); load(); }} />
      )}
      {editing && (
        <EditPoolEntryModal leagueId={leagueId} seasonId={seasonId} entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </>
  );
}

function FilterPill({ active, onClick, count, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 10px', borderRadius: 999,
      border: `1px solid ${active ? 'var(--ice-a40)' : 'var(--border)'}`,
      background: active ? 'var(--ice-a10)' : 'transparent',
      color: active ? 'var(--text)' : 'var(--text-2)',
      fontSize: 12, fontWeight: 500, cursor: 'pointer',
    }}>
      {children}
      <span style={{
        fontSize: 10, fontWeight: 700,
        padding: '1px 6px', borderRadius: 8,
        background: active ? 'var(--ice-a40)' : 'var(--surface-2)',
      }}>
        {count}
      </span>
    </button>
  );
}

function PoolEmpty({ canManage, hasFilter, onAdd, onImport }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 36 }}>
      <Users size={36} color="var(--text-2)" style={{ margin: '0 auto 14px', opacity: 0.5 }} />
      {hasFilter ? (
        <>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>No matches</div>
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>Try clearing the search or filter.</div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>No players in the pool yet</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 18, maxWidth: 420, margin: '0 auto 18px' }}>
            Add players manually or paste in a CSV. Once they're in the pool, allocate them to teams from the "By team" tab.
          </div>
          {canManage && (
            <div style={{ display: 'inline-flex', gap: 8 }}>
              <button type="button" className="btn-ghost" onClick={onImport}>
                <Upload size={13} style={{ marginRight: 6 }} />
                Import CSV
              </button>
              <button type="button" className="btn-ice" onClick={onAdd}>
                <UserPlus size={13} style={{ marginRight: 6 }} />
                Add player
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const STATUS_META = {
  available:  { label: 'Available',  color: 'var(--ice)' },
  assigned:   { label: 'On a team',  color: 'var(--goal)' },
  free_agent: { label: 'Free agent', color: 'var(--puck)' },
  withdrawn:  { label: 'Withdrawn',  color: 'var(--text-2)' },
};

function PoolRow({ entry, isLast, canManage, onEdit, onChanged }) {
  // Note: row-level delete intentionally not exposed. Withdrawing
  // a player is done from the edit modal (more deliberate + lets
  // the user reconsider via Cancel). Keeps the row UI uncluttered.
  const meta = STATUS_META[entry.status] || STATUS_META.available;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '12px 16px',
      borderBottom: isLast ? 'none' : '1px solid var(--border)',
    }}>
      <Avatar name={entry.display_name} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{entry.display_name}</div>
        <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--font-mono)' }}>{entry.player_uid}</span>
          {entry.position_pref && <span>· {entry.position_pref}</span>}
          {entry.skill_tier && <span>· tier {entry.skill_tier}</span>}
          {entry.jersey_number_pref != null && <span>· #{entry.jersey_number_pref}</span>}
          {!entry.user_id && <span style={{ color: 'var(--puck)' }}>· unclaimed</span>}
        </div>
      </div>
      <span className="badge" style={{
        background: 'transparent', border: `1px solid ${meta.color}`, color: meta.color,
        fontSize: 10, padding: '2px 8px', whiteSpace: 'nowrap',
      }}>
        {entry.status === 'assigned' && entry.current_team
          ? `On: ${entry.current_team.team_name}`
          : meta.label}
      </span>
      {canManage && (
        <button type="button" className="btn-ghost" style={tinyBtn} onClick={onEdit}>
          <Edit2 size={11} />
        </button>
      )}
    </div>
  );
}

function Avatar({ name }) {
  const initials = (name || '?').split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase() || '').join('') || '?';
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%',
      background: 'var(--surface-2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      fontSize: 12, fontWeight: 700, color: 'var(--text-2)',
    }}>
      {initials}
    </div>
  );
}

// ─── Add player modal ─────────────────────────────────────────

function AddPlayerModal({ leagueId, seasonId, onClose, onAdded }) {
  const [mode, setMode] = useState('email');  // 'email' | 'uid' | 'name'
  const [email, setEmail] = useState('');
  const [uid, setUid]   = useState('');
  const [name, setName] = useState('');
  const [position, setPosition] = useState('');
  const [skill, setSkill]       = useState('');
  const [jersey, setJersey]     = useState('');
  const [size, setSize]         = useState('');
  const [saving, setSaving]     = useState(false);

  async function submit() {
    const body = {};
    if (mode === 'email') {
      if (!email.trim() || !email.includes('@')) return toast.error('Valid email required');
      body.email = email.trim();
    } else if (mode === 'uid') {
      const v = uid.trim().toUpperCase();
      if (!/^PLR-[A-Z0-9]{8}$/.test(v)) return toast.error('Player UID looks like PLR-XXXXXXXX');
      body.player_uid = v;
    } else {
      if (!name.trim()) return toast.error('Display name required');
      body.display_name = name.trim();
    }
    if (position) body.position_pref = position;
    if (skill) {
      const n = parseInt(skill, 10);
      if (!Number.isInteger(n) || n < 1 || n > 5) {
        return toast.error('Skill tier must be 1-5');
      }
      body.skill_tier = n;
    }
    if (jersey) {
      const n = parseInt(jersey, 10);
      if (!Number.isInteger(n) || n < 0 || n > 999) {
        return toast.error('Jersey number must be 0-999');
      }
      body.jersey_number_pref = n;
    }
    if (size.trim()) body.jersey_size = size.trim();

    setSaving(true);
    try {
      await api.post(`/leagues/${leagueId}/seasons/${seasonId}/players/pool`, body);
      toast.success('Added to pool');
      onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not add player');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      {/* maxHeight uses var(--mobile-h) (falls back to 100dvh) instead of
          a plain vh unit: on mobile Safari/Capacitor, 100vh doesn't shrink
          when the keyboard opens, so a plain '90vh' can push the Save button
          off-screen behind the keyboard. var(--mobile-h) tracks the real
          visible viewport; desktop is unaffected (dvh ≈ vh there). */}
      <div className="card" style={{ maxWidth: 520, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto' }}>
        <ModalHeader title="Add player to pool" onClose={onClose} />

        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          <ModeButton active={mode === 'email'} onClick={() => setMode('email')} icon={Mail}>Email</ModeButton>
          <ModeButton active={mode === 'uid'}   onClick={() => setMode('uid')}   icon={Hash}>Player UID</ModeButton>
          <ModeButton active={mode === 'name'}  onClick={() => setMode('name')}  icon={Users}>Name only</ModeButton>
        </div>

        {mode === 'email' && (
          <Field label="Account email"
            hint="Match an existing account by email, or create an unclaimed profile if none found.">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com" autoFocus style={inputStyle} />
          </Field>
        )}
        {mode === 'uid' && (
          <Field label="Player UID" hint="The PLR-XXXXXXXX code from their Game Day profile.">
            <input type="text" value={uid} onChange={(e) => setUid(e.target.value.toUpperCase())}
              placeholder="PLR-XXXXXXXX" maxLength={12} autoFocus
              style={{ ...inputStyle, fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }} />
          </Field>
        )}
        {mode === 'name' && (
          <Field label="Display name"
            hint="Creates a new unclaimed profile. The player can claim it later by registering with this name.">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Jane Smith" autoFocus style={inputStyle} />
          </Field>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <Field label="Position" style={{ flex: '1 1 140px' }}>
            <select value={position} onChange={(e) => setPosition(e.target.value)} style={inputStyle}>
              <option value="">—</option>
              <option value="forward">Forward</option>
              <option value="defense">Defense</option>
              <option value="goalie">Goalie</option>
              <option value="flex">Flex</option>
            </select>
          </Field>
          <Field label="Skill tier (1-5)" style={{ flex: '1 1 100px' }}>
            <input type="number" min={1} max={5} value={skill}
              onChange={(e) => setSkill(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Jersey #" style={{ flex: '1 1 100px' }}>
            <input type="number" min={0} max={999} value={jersey}
              onChange={(e) => setJersey(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Jersey size" style={{ flex: '1 1 100px' }}>
            <input type="text" value={size} maxLength={20}
              onChange={(e) => setSize(e.target.value)}
              placeholder="M, L, XL…" style={inputStyle} />
          </Field>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-ice" onClick={submit} disabled={saving}>
            {saving ? 'Adding…' : 'Add to pool'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

// ─── Edit pool entry modal ────────────────────────────────────

function EditPoolEntryModal({ leagueId, seasonId, entry, onClose, onSaved }) {
  const [position, setPosition] = useState(entry.position_pref || '');
  const [skill, setSkill]       = useState(entry.skill_tier ?? '');
  const [jersey, setJersey]     = useState(entry.jersey_number_pref ?? '');
  const [size, setSize]         = useState(entry.jersey_size || '');
  const [status, setStatus]     = useState(entry.status);
  const [saving, setSaving]     = useState(false);

  // Status can only be changed by the API if NOT currently 'assigned'.
  // Mirror that here.
  const statusEditable = entry.status !== 'assigned';

  async function save() {
    setSaving(true);
    try {
      const body = {};
      body.position_pref      = position || null;
      body.skill_tier         = skill === '' ? null : parseInt(skill, 10);
      body.jersey_number_pref = jersey === '' ? null : parseInt(jersey, 10);
      body.jersey_size        = size.trim() || null;
      if (statusEditable && status !== entry.status) body.status = status;
      await api.patch(`/leagues/${leagueId}/seasons/${seasonId}/players/pool/${entry.id}`, body);
      toast.success('Saved');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  async function withdraw() {
    if (!window.confirm(`Withdraw ${entry.display_name} from the pool?`)) return;
    try {
      await api.delete(`/leagues/${leagueId}/seasons/${seasonId}/players/pool/${entry.id}`);
      toast.success('Player withdrawn');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not withdraw');
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      {/* mobile-h (not 100vh) so the keyboard doesn't clip the Save/Withdraw
          footer — same fix as AddPlayerModal above. */}
      <div className="card" style={{ maxWidth: 520, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto' }}>
        <ModalHeader title={`Edit ${entry.display_name}`} onClose={onClose} />

        <div style={{
          padding: 10, background: 'var(--surface-2)', borderRadius: 8,
          fontSize: 12, color: 'var(--text-2)', marginBottom: 14,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Hash size={12} />
          <span style={{ fontFamily: 'var(--font-mono)' }}>{entry.player_uid}</span>
          {!entry.user_id && <span style={{ color: 'var(--puck)', marginLeft: 'auto' }}>Unclaimed profile</span>}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <Field label="Position" style={{ flex: '1 1 140px' }}>
            <select value={position} onChange={(e) => setPosition(e.target.value)} style={inputStyle}>
              <option value="">—</option>
              <option value="forward">Forward</option>
              <option value="defense">Defense</option>
              <option value="goalie">Goalie</option>
              <option value="flex">Flex</option>
            </select>
          </Field>
          <Field label="Skill tier (1-5)" style={{ flex: '1 1 100px' }}>
            <input type="number" min={1} max={5} value={skill}
              onChange={(e) => setSkill(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Jersey #" style={{ flex: '1 1 100px' }}>
            <input type="number" min={0} max={999} value={jersey}
              onChange={(e) => setJersey(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Jersey size" style={{ flex: '1 1 100px' }}>
            <input type="text" value={size} maxLength={20}
              onChange={(e) => setSize(e.target.value)} style={inputStyle} />
          </Field>
        </div>

        <Field label="Status" hint={!statusEditable ? "Assigned players must be unassigned (in the By team tab) before status can be changed." : null}>
          <select value={status} onChange={(e) => setStatus(e.target.value)} disabled={!statusEditable} style={inputStyle}>
            <option value="available">Available</option>
            {entry.status === 'assigned' && <option value="assigned">On a team</option>}
            <option value="free_agent">Free agent</option>
            <option value="withdrawn">Withdrawn</option>
          </select>
        </Field>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
          {entry.status !== 'assigned' && entry.status !== 'withdrawn' && (
            <button type="button" className="btn-ghost"
              onClick={withdraw} disabled={saving}
              style={{ color: 'var(--penalty)', borderColor: 'var(--penalty-a30)' }}>
              <Trash2 size={12} style={{ marginRight: 6 }} />
              Withdraw
            </button>
          )}
          <div style={{ display: 'flex', gap: 10, marginLeft: 'auto' }}>
            <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" className="btn-ice" onClick={save} disabled={saving}>
              <Save size={12} style={{ marginRight: 6 }} />
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}

// ─── CSV import modal (preview + commit) ──────────────────────

function ImportCsvModal({ leagueId, seasonId, onClose, onCompleted }) {
  const [step, setStep] = useState('paste');   // 'paste' | 'preview' | 'done'
  const [csv, setCsv]   = useState('');
  const [previewResult, setPreviewResult] = useState(null);  // {summary, plan, errors}
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted]   = useState(null);  // commit response

  async function readFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('File too large (>5MB)');
      return;
    }
    const text = await f.text();
    setCsv(text);
  }

  async function runPreview() {
    if (!csv.trim()) return toast.error('Paste CSV text or pick a file');
    try {
      const { data } = await api.post(
        `/leagues/${leagueId}/seasons/${seasonId}/players/import/csv/preview`,
        { csv }
      );
      setPreviewResult(data);
      setStep('preview');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Preview failed');
    }
  }

  async function runCommit() {
    setCommitting(true);
    try {
      const { data } = await api.post(
        `/leagues/${leagueId}/seasons/${seasonId}/players/import/csv/commit`,
        { csv, confirm: true }
      );
      setCommitted(data);
      setStep('done');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Commit failed');
    } finally {
      setCommitting(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      {/* mobile-h (not 100vh) — this modal has a CSV textarea + a scrollable
          plan list; on a phone with the keyboard up, a plain vh cap left the
          Import/Back footer unreachable. */}
      <div className="card" style={{ maxWidth: 720, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.92)', overflowY: 'auto' }}>
        <ModalHeader title="Import players from CSV" onClose={onClose} />

        {step === 'paste' && (
          <ImportPasteStep csv={csv} setCsv={setCsv}
            onFile={readFile} onNext={runPreview}
            onCancel={onClose} />
        )}
        {step === 'preview' && previewResult && (
          <ImportPreviewStep
            result={previewResult}
            onBack={() => setStep('paste')}
            onCommit={runCommit}
            committing={committing}
          />
        )}
        {step === 'done' && committed && (
          <ImportDoneStep summary={committed.summary} errors={committed.errors || []}
            onClose={onCompleted} />
        )}
      </div>
    </ModalBackdrop>
  );
}

function ImportPasteStep({ csv, setCsv, onFile, onNext, onCancel }) {
  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 14 }}>
        Upload or paste a CSV with a header row. Columns we recognize:
      </p>
      <ul style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 14, paddingLeft: 20, lineHeight: 1.7 }}>
        <li><code>name</code> — full name (or <code>full name</code>, <code>player name</code>)</li>
        <li><code>email</code> — used to match existing accounts</li>
        <li><code>position</code> — forward / defense / goalie / flex (single letters work too: F/D/G)</li>
        <li><code>skill</code> — 1 (elite) to 5 (beginner)</li>
        <li><code>jersey</code>, <code>size</code> — optional metadata</li>
      </ul>
      <p style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 14 }}>
        Either <code>name</code> or <code>email</code> is required. Other columns optional. Unknown
        columns are ignored.
      </p>

      <Field label="Upload a .csv file">
        <input type="file" accept=".csv,text/csv" onChange={onFile}
          style={{ ...inputStyle, padding: 6 }} />
      </Field>
      <Field label="Or paste CSV text" hint="The first row should be the header.">
        <textarea value={csv} onChange={(e) => setCsv(e.target.value)}
          rows={8} placeholder="name,email,position,skill,jersey&#10;Jane Smith,jane@example.com,forward,2,17&#10;…"
          style={{ ...inputStyle, fontFamily: 'var(--font-mono)', fontSize: 12, resize: 'vertical' }} />
      </Field>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn-ice" onClick={onNext} disabled={!csv.trim()}>
          Preview
          <ArrowRight size={13} style={{ marginLeft: 6 }} />
        </button>
      </div>
    </>
  );
}

function ImportPreviewStep({ result, onBack, onCommit, committing }) {
  const { summary, plan, errors } = result;
  const willImport = summary.will_import || 0;
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 14 }}>
        <SummaryCard label="Will import"           value={willImport}                       color="var(--goal)" />
        <SummaryCard label="Match existing"        value={summary.match_existing_profile}   color="var(--ice)" />
        <SummaryCard label="Create new profile"    value={summary.create_new_profile}       color="var(--ice)" />
        <SummaryCard label="Skip (already in pool)" value={summary.already_in_pool}         color="var(--text-2)" />
        <SummaryCard label="Skip (duplicate row)"  value={summary.duplicate_in_upload}      color="var(--text-2)" />
        <SummaryCard label="Invalid"               value={summary.invalid}                  color="var(--penalty)" />
      </div>

      {errors.length > 0 && (
        <div style={{
          background: 'var(--penalty-a15)', borderLeft: '3px solid var(--penalty)',
          borderRadius: 8, padding: 10, marginBottom: 14,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--penalty)', marginBottom: 4 }}>
            {errors.length} parse error{errors.length === 1 ? '' : 's'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-2)', maxHeight: 80, overflowY: 'auto' }}>
            {errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
            {errors.length > 5 && <div>…and {errors.length - 5} more</div>}
          </div>
        </div>
      )}

      <details style={{ marginBottom: 14 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-2)' }}>
          Row-by-row plan ({plan.length} rows)
        </summary>
        <div style={{
          maxHeight: 260, overflowY: 'auto',
          marginTop: 8, border: '1px solid var(--border)', borderRadius: 8,
        }}>
          {plan.map((row, i) => (
            <PlanRow key={i} row={row} />
          ))}
        </div>
      </details>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn-ghost" onClick={onBack} disabled={committing}>
          Back
        </button>
        <button type="button" className="btn-ice" onClick={onCommit} disabled={committing || willImport === 0}>
          {committing ? 'Importing…' : `Import ${willImport} player${willImport === 1 ? '' : 's'}`}
        </button>
      </div>
    </>
  );
}

function PlanRow({ row }) {
  const ACTION_META = {
    match_existing_profile: { Icon: CheckCircle2, color: 'var(--ice)',     label: 'Match existing' },
    create_new_profile:     { Icon: Plus,         color: 'var(--goal)',    label: 'Create new' },
    already_in_pool:        { Icon: CheckCircle2, color: 'var(--text-2)',  label: 'Already in pool' },
    duplicate_in_upload:    { Icon: AlertTriangle,color: 'var(--puck)',    label: 'Duplicate' },
    invalid:                { Icon: X,            color: 'var(--penalty)', label: 'Invalid' },
  };
  const meta = ACTION_META[row.action] || ACTION_META.invalid;
  const Icon = meta.Icon;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '6px 10px', borderBottom: '1px solid var(--border)',
      fontSize: 12,
    }}>
      <Icon size={12} color={meta.color} style={{ flexShrink: 0 }} />
      <span style={{ color: 'var(--text-3)', fontFamily: 'var(--font-mono)', minWidth: 30 }}>{row.line}</span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {row.name || '(no name)'}
        {row.email && <span style={{ color: 'var(--text-3)' }}> · {row.email}</span>}
        {row.matched_name && row.matched_name !== row.name && (
          <span style={{ color: 'var(--text-3)' }}> → {row.matched_name}</span>
        )}
      </span>
      <span style={{ color: meta.color, fontSize: 11, fontWeight: 600 }}>{meta.label}</span>
    </div>
  );
}

function ImportDoneStep({ summary, errors, onClose }) {
  return (
    <>
      <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
        <CheckCircle2 size={40} color="var(--goal)" style={{ margin: '0 auto 10px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Import complete</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
          Imported <strong style={{ color: 'var(--text)' }}>{summary.imported}</strong> player
          {summary.imported === 1 ? '' : 's'}
          {' '}({summary.matched_existing} matched, {summary.created_profiles} new profile{summary.created_profiles === 1 ? '' : 's'})
          {summary.skipped > 0 && <>, skipped {summary.skipped}</>}.
        </div>
      </div>
      {errors.length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--puck)' }}>
            {errors.length} error{errors.length === 1 ? '' : 's'} during import
          </summary>
          <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 6, maxHeight: 120, overflowY: 'auto', padding: 8, background: 'var(--surface-2)', borderRadius: 6 }}>
            {errors.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        </details>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn-ice" onClick={onClose}>Done</button>
      </div>
    </>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{
      padding: 12, borderRadius: 10,
      background: 'var(--surface-2)', border: '1px solid var(--border)',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// BY TEAM TAB
// ═══════════════════════════════════════════════════════════════

function ByTeamTab({ leagueId, seasonId, canManage }) {
  const [data, setData]       = useState(null);   // { teams: [...] }
  const [pool, setPool]       = useState([]);     // available players
  const [loading, setLoading] = useState(true);
  const [allocating, setAllocating] = useState(null);  // pool entry being allocated
  const [transferring, setTransferring] = useState(null);  // { pool_id, current_team_id, name }

  async function load() {
    setLoading(true);
    try {
      const [byTeamRes, poolRes] = await Promise.all([
        api.get(`/leagues/${leagueId}/seasons/${seasonId}/players/by-team`),
        api.get(`/leagues/${leagueId}/seasons/${seasonId}/players/pool?status=available`),
      ]);
      setData(byTeamRes.data);
      setPool(poolRes.data?.pool || []);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error('Could not load rosters');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [seasonId]);

  async function unassign(member, teamName) {
    if (!window.confirm(`Remove ${member.player_name} from ${teamName}? They'll go back to the pool.`)) return;
    try {
      await api.post(`/leagues/${leagueId}/seasons/${seasonId}/players/unassign`,
        { pool_id: member.pool_id });
      toast.success('Returned to pool');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not unassign');
    }
  }

  if (loading) return <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>;
  if (!data?.teams || data.teams.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 36, color: 'var(--text-2)', fontSize: 13 }}>
        <Users size={36} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
        No teams in this season yet.{' '}
        <Link to={`/league/${leagueId}/seasons`} style={{ color: 'var(--ice)' }}>
          Invite teams from the Seasons tab →
        </Link>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
          {pool.length} player{pool.length === 1 ? '' : 's'} available in pool
          {pool.length > 0 && canManage && (
            <>{' '}— click "Allocate" on a team to add one.</>
          )}
        </span>
        <button type="button" className="btn-ghost" onClick={load}>
          <RefreshCw size={12} style={{ marginRight: 6 }} />
          Refresh
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {data.teams.map(t => (
          <TeamRosterCard key={t.team_id} team={t}
            canManage={canManage}
            poolCount={pool.length}
            onAllocate={() => setAllocating({ teamId: t.team_id, teamName: t.team_name })}
            onTransfer={(m) => setTransferring({
              pool_id: m.pool_id, current_team_id: t.team_id, name: m.player_name,
            })}
            onUnassign={(m) => unassign(m, t.team_name)} />
        ))}
      </div>

      {allocating && (
        <AllocateModal leagueId={leagueId} seasonId={seasonId}
          teamId={allocating.teamId} teamName={allocating.teamName}
          pool={pool}
          onClose={() => setAllocating(null)}
          onDone={() => { setAllocating(null); load(); }} />
      )}
      {transferring && (
        <TransferModal leagueId={leagueId} seasonId={seasonId}
          poolId={transferring.pool_id} currentTeamId={transferring.current_team_id}
          playerName={transferring.name}
          teams={data.teams}
          onClose={() => setTransferring(null)}
          onDone={() => { setTransferring(null); load(); }} />
      )}
    </>
  );
}

function TeamRosterCard({ team, canManage, poolCount, onAllocate, onTransfer, onUnassign }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 16px', borderBottom: '1px solid var(--border)',
        background: 'var(--surface-2)',
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: 'var(--ice-a10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Users size={14} color="var(--ice)" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{team.team_name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
            {team.division_name || 'No division'}
            {' · '}
            {team.roster.length} player{team.roster.length === 1 ? '' : 's'}
          </div>
        </div>
        {canManage && (
          <button type="button" className="btn-ice"
            onClick={onAllocate}
            disabled={poolCount === 0}
            style={{ fontSize: 12, padding: '6px 12px' }}>
            <UserPlus size={12} style={{ marginRight: 6 }} />
            Allocate
          </button>
        )}
      </div>
      {team.roster.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic', padding: '14px 16px' }}>
          No players on this team yet.
        </div>
      ) : (
        <div>
          {team.roster.map((m, i) => (
            <div key={m.team_member_id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '8px 16px',
              borderBottom: i === team.roster.length - 1 ? 'none' : '1px solid var(--border)',
            }}>
              <Avatar name={m.player_name} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {m.jersey_number != null && (
                    <span style={{ color: 'var(--ice)', marginRight: 6 }}>#{m.jersey_number}</span>
                  )}
                  {m.player_name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 1 }}>
                  {m.position || '—'}
                  {m.rating && <> · tier {m.rating}</>}
                  {' · '}<span style={{ fontFamily: 'var(--font-mono)' }}>{m.player_uid}</span>
                </div>
              </div>
              {canManage && (
                <div style={{ display: 'flex', gap: 4 }}>
                  <button type="button" className="btn-ghost" style={tinyBtn}
                    onClick={() => onTransfer(m)} title="Transfer to another team">
                    <ArrowLeftRight size={11} />
                  </button>
                  <button type="button" className="btn-ghost"
                    style={{ ...tinyBtn, color: 'var(--penalty)' }}
                    onClick={() => onUnassign(m)} title="Return to pool">
                    <Trash2 size={11} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AllocateModal({ leagueId, seasonId, teamId, teamName, pool, onClose, onDone }) {
  const [chosenId, setChosenId] = useState('');
  const [position, setPosition] = useState('');
  const [jersey, setJersey]     = useState('');
  const [rating, setRating]     = useState('');
  const [search, setSearch]     = useState('');
  const [saving, setSaving]     = useState(false);

  // When choosing a pool entry, pre-fill from their pool preferences.
  const chosen = useMemo(() => pool.find(p => p.id === chosenId), [pool, chosenId]);
  useEffect(() => {
    if (chosen) {
      setPosition(chosen.position_pref || '');
      setJersey(chosen.jersey_number_pref != null ? String(chosen.jersey_number_pref) : '');
      setRating(chosen.skill_tier != null ? String(chosen.skill_tier) : '');
    }
  }, [chosen]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(p =>
      (p.display_name || '').toLowerCase().includes(q) ||
      (p.player_uid   || '').toLowerCase().includes(q)
    );
  }, [pool, search]);

  async function submit() {
    if (!chosenId) return toast.error('Pick a player');
    const body = { pool_id: chosenId, team_id: teamId };
    if (position) body.position = position;
    if (jersey)   body.jersey_number = parseInt(jersey, 10);
    if (rating) {
      const n = parseInt(rating, 10);
      if (Number.isInteger(n) && n >= 1 && n <= 5) body.rating = n;
    }
    setSaving(true);
    try {
      await api.post(`/leagues/${leagueId}/seasons/${seasonId}/players/allocate`, body);
      toast.success(`Added to ${teamName}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not allocate');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      {/* mobile-h (not 100vh) — same keyboard-clipping fix as the other
          player modals in this file. */}
      <div className="card" style={{ maxWidth: 560, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.92)', overflowY: 'auto' }}>
        <ModalHeader title={`Allocate to ${teamName}`} onClose={onClose} />

        <Field label="Choose player from pool">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…" style={inputStyle} />
        </Field>
        <div style={{
          maxHeight: 200, overflowY: 'auto',
          border: '1px solid var(--border)', borderRadius: 10,
          marginBottom: 14,
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 14, fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }}>
              No available players match.
            </div>
          ) : (
            filtered.map(p => (
              <button key={p.id} type="button" onClick={() => setChosenId(p.id)}
                style={{
                  width: '100%', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px',
                  border: 'none', cursor: 'pointer',
                  background: chosenId === p.id ? 'var(--ice-a10)' : 'transparent',
                  color: 'var(--text)',
                  borderBottom: '1px solid var(--border)',
                }}>
                <Avatar name={p.display_name} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{p.display_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
                    {p.position_pref || '—'}
                    {p.skill_tier && <> · tier {p.skill_tier}</>}
                    {!p.user_id && <span style={{ color: 'var(--puck)' }}> · unclaimed</span>}
                  </div>
                </div>
                {chosenId === p.id && <CheckCircle2 size={14} color="var(--ice)" />}
              </button>
            ))
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <Field label="Position" style={{ flex: '1 1 140px' }}>
            <select value={position} onChange={(e) => setPosition(e.target.value)} style={inputStyle}>
              <option value="">—</option>
              <option value="forward">Forward</option>
              <option value="defense">Defense</option>
              <option value="goalie">Goalie</option>
              <option value="flex">Flex</option>
            </select>
          </Field>
          <Field label="Jersey #" style={{ flex: '1 1 100px' }}>
            <input type="number" min={0} max={999} value={jersey}
              onChange={(e) => setJersey(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Rating (1-5)" style={{ flex: '1 1 100px' }}>
            <input type="number" min={1} max={5} value={rating}
              onChange={(e) => setRating(e.target.value)} style={inputStyle} />
          </Field>
        </div>

        {chosen && !chosen.user_id && (
          <div style={{
            padding: 10, background: 'var(--puck-a15)', borderRadius: 8,
            fontSize: 12, color: 'var(--text-2)', marginBottom: 14,
            display: 'flex', gap: 8, alignItems: 'flex-start',
          }}>
            <AlertTriangle size={14} color="var(--puck)" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              {/* Phase A (league gap-closure): unclaimed profiles CAN now be
                  allocated — the roster row links up automatically when the
                  player verifies an account with the email on file. */}
              This player hasn't signed up yet (unclaimed profile). You can still
              allocate them — their roster spot links to their account automatically
              once they register and verify the email on file.
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-ice" onClick={submit} disabled={saving || !chosenId}>
            {saving ? 'Allocating…' : 'Allocate'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function TransferModal({ leagueId, seasonId, poolId, currentTeamId, playerName, teams, onClose, onDone }) {
  const [toTeamId, setToTeamId] = useState('');
  const [reason, setReason]     = useState('');
  const [saving, setSaving]     = useState(false);

  const targets = teams.filter(t => t.team_id !== currentTeamId);

  async function submit() {
    if (!toTeamId) return toast.error('Pick a destination team');
    setSaving(true);
    try {
      await api.post(`/leagues/${leagueId}/seasons/${seasonId}/players/transfer`,
        { pool_id: poolId, to_team_id: toTeamId, reason: reason.trim() || null });
      toast.success('Transfer complete');
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not transfer');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      {/* Short form, but cap + scroll anyway so it can't get clipped behind
          the mobile keyboard when the "Reason" field is focused. */}
      <div className="card" style={{ maxWidth: 460, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto' }}>
        <ModalHeader title={`Transfer ${playerName}`} onClose={onClose} />

        <Field label="Move to team">
          <select value={toTeamId} onChange={(e) => setToTeamId(e.target.value)} style={inputStyle}>
            <option value="">Choose a team…</option>
            {targets.map(t => (
              <option key={t.team_id} value={t.team_id}>{t.team_name}</option>
            ))}
          </select>
        </Field>

        <Field label="Reason (optional, for audit log)">
          <input type="text" value={reason} maxLength={200}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. 'Balancing rosters'" style={inputStyle} />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-ice" onClick={submit} disabled={saving || !toTeamId}>
            {saving ? 'Transferring…' : 'Transfer'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

// ═══════════════════════════════════════════════════════════════
// TRANSFERS TAB
// ═══════════════════════════════════════════════════════════════

function TransfersTab({ leagueId, seasonId }) {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/leagues/${leagueId}/seasons/${seasonId}/players/transfers`);
      setTransfers(data?.transfers || []);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error('Could not load transfers');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [seasonId]);

  if (loading) return <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>;
  if (transfers.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 36, color: 'var(--text-2)', fontSize: 13 }}>
        <FileText size={36} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
        No transfers yet. Allocations and moves between teams will show up here.
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {transfers.map((t, i) => (
        <div key={t.id} style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 16px',
          borderBottom: i === transfers.length - 1 ? 'none' : '1px solid var(--border)',
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: t.from_team_id ? (t.to_team_id ? 'var(--ice-a10)' : 'var(--puck-a15)') : 'var(--goal-a20)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {t.from_team_id && t.to_team_id ? <ArrowLeftRight size={13} color="var(--ice)" />
              : t.to_team_id ? <UserPlus size={13} color="var(--goal)" />
              : <ArrowRight size={13} color="var(--puck)" style={{ transform: 'rotate(180deg)' }} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{t.player_name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
              {t.from_team_name ? <strong style={{ color: 'var(--text)' }}>{t.from_team_name}</strong> : <em>Pool</em>}
              {' → '}
              {t.to_team_name ? <strong style={{ color: 'var(--text)' }}>{t.to_team_name}</strong> : <em>Pool</em>}
              {t.reason && <> · {t.reason}</>}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
              {formatDateTime(t.moved_at)} · by {t.moved_by_name}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function formatDateTime(s) {
  if (!s) return '';
  try {
    const d = new Date(s);
    return d.toLocaleString();
  } catch { return s; }
}

// ═══════════════════════════════════════════════════════════════
// Shared bits
// ═══════════════════════════════════════════════════════════════

function ModalBackdrop({ onClose, children }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, zIndex: 100,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        {children}
      </div>
    </div>
  );
}
function ModalHeader({ title, onClose }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <h2 className="section-title text-lg">{title}</h2>
      <button type="button" onClick={onClose}
        style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer' }}>
        <X size={18} />
      </button>
    </div>
  );
}
function ModeButton({ active, onClick, icon: Icon, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      flex: '1 1 100px', padding: '8px 12px', borderRadius: 8,
      background: active ? 'var(--ice-a10)' : 'var(--surface-2)',
      border: `1px solid ${active ? 'var(--ice-a40)' : 'var(--border)'}`,
      color: active ? 'var(--text)' : 'var(--text-2)',
      fontWeight: 600, fontSize: 12, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    }}>
      <Icon size={13} />
      {children}
    </button>
  );
}
function Field({ label, hint, children, style }) {
  return (
    <label style={{ display: 'block', marginBottom: 12, ...style }}>
      <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
        {label}
      </span>
      {children}
      {hint && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{hint}</span>}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '8px 12px',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  outline: 'none',
};
const tinyBtn = { padding: '4px 8px', fontSize: 11, minHeight: 'auto' };

// ─────────────────────────────────────────────────────────────────
// Phase H (league gap-closure, 2026-06-09) — Discipline tab.
// League-scoped (not per-season): a suspension follows the player
// across the league. Backend: routes/league-suspensions.js. Game
// sheets surface a ⚠ badge for lineups containing an actively
// suspended player.
// ─────────────────────────────────────────────────────────────────
function DisciplineTab({ leagueId, seasonId, canManage }) {
  const [items, setItems] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    try {
      const { data } = await api.get(`/leagues/${leagueId}/suspensions?status=all`);
      setItems(data?.suspensions || []);
    } catch {
      setItems([]);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leagueId]);

  async function patch(s, body, confirmMsg) {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusyId(s.id);
    try {
      await api.patch(`/leagues/${leagueId}/suspensions/${s.id}`, body);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Update failed');
    } finally {
      setBusyId(null);
    }
  }

  const visible = (items || []).filter(s => showResolved || s.status === 'active');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--text-2)', flex: 1 }}>
          Suspensions are league-wide. Game sheets warn when a suspended player is in a lineup.
        </span>
        <label style={{ fontSize: 12, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
          Show resolved
        </label>
        {canManage && (
          <button className="btn-ice" style={tinyBtn} onClick={() => setCreating(true)}>
            <Plus size={12} /> New suspension
          </button>
        )}
      </div>

      {items === null && <div style={{ color: 'var(--text-2)', fontSize: 13 }}>Loading…</div>}
      {items !== null && visible.length === 0 && (
        <div style={{ color: 'var(--text-2)', fontSize: 13 }}>
          {showResolved ? 'No suspensions on record.' : 'No active suspensions.'}
        </div>
      )}
      {visible.map(s => (
        <div key={s.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Avatar name={s.player_name} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                {s.player_name}
                <span style={{
                  marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                  background: s.status === 'active' ? 'rgba(255,68,68,0.12)' : 'var(--surface-2)',
                  color: s.status === 'active' ? 'var(--penalty, #ff4444)' : 'var(--text-2)',
                }}>
                  {s.status === 'active'
                    ? (s.games_total != null
                        ? `suspended · ${s.games_served}/${s.games_total} served`
                        : 'suspended · indefinite')
                    : s.status}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
                {s.reason} · issued {new Date(s.issued_at).toLocaleDateString()} by {s.issued_by_name}
              </div>
              {s.notes && <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{s.notes}</div>}
            </div>
            {canManage && s.status === 'active' && (
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn-ghost" style={tinyBtn} disabled={busyId === s.id}
                  onClick={() => patch(s, { serve_one: true })}>
                  Served a game
                </button>
                <button className="btn-ghost" style={{ ...tinyBtn, color: 'var(--penalty, #ff4444)' }} disabled={busyId === s.id}
                  onClick={() => patch(s, { status: 'rescinded' }, 'Rescind this suspension?')}>
                  Rescind
                </button>
              </div>
            )}
          </div>
        </div>
      ))}

      {creating && (
        <NewSuspensionModal
          leagueId={leagueId} seasonId={seasonId}
          onClose={() => setCreating(false)}
          onDone={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function NewSuspensionModal({ leagueId, seasonId, onClose, onDone }) {
  const [pool, setPool] = useState([]);
  const [profileId, setProfileId] = useState('');
  const [reason, setReason] = useState('');
  const [games, setGames] = useState('2');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Player picker = the current season's pool (the backend accepts
  // any profile in any of the league's seasons; the current pool is
  // the practical 99% case).
  useEffect(() => {
    if (!seasonId) return;
    let cancelled = false;
    api.get(`/leagues/${leagueId}/seasons/${seasonId}/players/pool?status=all`)
      .then(({ data }) => { if (!cancelled) setPool(data?.entries || data?.pool || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [leagueId, seasonId]);

  async function save() {
    if (!profileId) return toast.error('Pick a player');
    if (!reason.trim()) return toast.error('Reason is required');
    const g = games.trim() === '' ? null : parseInt(games, 10);
    if (g !== null && (!Number.isInteger(g) || g < 1 || g > 99)) {
      return toast.error('Games must be 1-99 (or blank for indefinite)');
    }
    setSaving(true);
    try {
      await api.post(`/leagues/${leagueId}/suspensions`, {
        player_profile_id: profileId,
        reason: reason.trim(),
        games_total: g,
        notes: notes.trim() || null,
      });
      toast.success('Suspension recorded');
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not create suspension');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      {/* Same mobile-keyboard safety cap as TransferModal — 4 fields can run
          close to the fold on short phones once the keyboard is up. */}
      <div className="card" style={{ maxWidth: 460, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto' }}>
        <ModalHeader title="New suspension" onClose={onClose} />
        <Field label="Player">
          <select value={profileId} onChange={(e) => setProfileId(e.target.value)} style={inputStyle}>
            <option value="">Choose a player…</option>
            {pool.map(p => (
              <option key={p.player_profile_id} value={p.player_profile_id}>{p.display_name}</option>
            ))}
          </select>
        </Field>
        <Field label="Reason">
          <input type="text" value={reason} maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. 'Boarding — match penalty, game #14'" style={inputStyle} />
        </Field>
        <Field label="Games (blank = indefinite)">
          <input type="number" min={1} max={99} value={games}
            onChange={(e) => setGames(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Notes (optional)">
          <input type="text" value={notes} maxLength={2000}
            onChange={(e) => setNotes(e.target.value)} style={inputStyle} />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-ice" onClick={save} disabled={saving || !profileId}>
            {saving ? 'Saving…' : 'Suspend'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
